import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import segnalaMancanti from '../../../components/Util/campiMancanti';
import { IMPOSTAZIONI_SIMULAZIONE, POSTA_VUOTA, inviaEmailDiProva, leggiPosta, salvaPosta, testaConnessione } from '../../../../api-client/impostazioniSistema';

/*
 * Impostazioni › Posta in uscita: il server SMTP che invia TUTTE le email del portale SportID
 * (docs/richieste-backend.md, capitolo 9). Si userà dopo la migrazione da AWS (oggi le email partono da AWS SES).
 *
 * Copia della pagina «Posta in uscita» di LUME (regola comune a tutti i progetti): un solo riquadro,
 * «Salva» memorizza tutto compresa l'email di test, le prove si fanno solo con tutti i dati compilati.
 */

/** Perché il sistema non può inviare email con questi dati; '' se può (stessa regola di LUME). */
const motivoNonInvio = (form) => {
    if (!form.enabled) return 'la casella «Usa questa configurazione per inviare le email» non è selezionata';
    const mancanti = [];
    if (!String(form.host || '').trim()) mancanti.push('server SMTP');
    if (!form.port) mancanti.push('porta');
    if (!String(form.from_address || '').trim()) mancanti.push('indirizzo mittente');
    return mancanti.length ? `dati obbligatori non compilati: ${mancanti.join(', ')}` : '';
};

export const PostaInUscita = () => {
    const [form, setForm] = useState(null);
    const [passwordSalvata, setPasswordSalvata] = useState(false);
    const [stato, setStato] = useState(null);
    const [esitoTest, setEsitoTest] = useState(null);
    const [esitoInvio, setEsitoInvio] = useState(null);
    const [inCorso, setInCorso] = useState('');

    useEffect(() => {
        leggiPosta().then((r) => {
            if (!r || r.error) { setStato({ ok: false, testo: r?.error || 'Errore nel caricamento della configurazione.' }); setForm({ ...POSTA_VUOTA }); return; }
            setPasswordSalvata(!!r.password_set);
            setForm({ ...POSTA_VUOTA, ...r, password: '' });
        });
    }, []);

    if (!form) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;

    const aggiorna = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const problema = motivoNonInvio(form);
    // Le prove si fanno solo con tutti i dati compilati (regola comune)
    const destinatarioOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.test_recipient || '').trim());
    // Dati mancanti per le prove: si evidenziano i campi, il tasto non si disabilita (set-ui «Dati mancanti»)
    const controllaProva = (conDestinatario) => {
        const mancanti = [];
        if (!String(form.from_address || '').trim()) mancanti.push({ campo: 'from_address', messaggio: 'Manca l\'indirizzo mittente' });
        if (!String(form.host || '').trim()) mancanti.push({ campo: 'host', messaggio: 'Manca il server SMTP' });
        if (!form.port) mancanti.push({ campo: 'port', messaggio: 'Manca la porta' });
        if (!String(form.username || '').trim()) mancanti.push({ campo: 'username', messaggio: 'Manca l\'utente' });
        if (!form.password && !passwordSalvata) mancanti.push({ campo: 'password', messaggio: 'Manca la password' });
        if (conDestinatario && !destinatarioOk) mancanti.push({ campo: 'test_recipient', messaggio: String(form.test_recipient || '').trim() ? 'Scrivi un indirizzo email valido' : 'Manca l\'indirizzo a cui inviare l\'email di test' });
        return segnalaMancanti(mancanti);
    };

    const salva = async (e) => {
        e.preventDefault();
        setStato(null);
        setInCorso('salva');
        const r = await salvaPosta(form);
        setInCorso('');
        if (r?.error) { setStato({ ok: false, testo: r.error }); return; }
        if (form.password) setPasswordSalvata(true);
        setForm((f) => ({ ...f, password: '' }));
        setStato({ ok: !problema, testo: problema ? 'Impostazioni salvate, ma il sistema non può inviare email con questa configurazione.' : 'Impostazioni salvate.' });
    };
    const prova = async () => {
        if (controllaProva(false) > 0) return;
        setEsitoTest(null);
        setInCorso('test');
        const r = await testaConnessione(form);
        setInCorso('');
        setEsitoTest({ ok: !r?.error, testo: r?.error || r?.message || 'Connessione riuscita.' });
    };
    const invia = async () => {
        if (controllaProva(true) > 0) return;
        setEsitoInvio(null);
        setInCorso('invio');
        const r = await inviaEmailDiProva(form);
        setInCorso('');
        setEsitoInvio({ ok: !r?.error, testo: r?.error || r?.message || `Email di test inviata a ${form.test_recipient}.` });
    };

    return (
        <Form onSubmit={salva} noValidate>
            {IMPOSTAZIONI_SIMULAZIONE && (
                <Alert variant="warning" className="text-dark">
                    <strong>Simulazione:</strong> la configurazione resta in questo browser e le prove non inviano nulla finché il
                    backend non ha le rotte. Questo server si userà dopo la migrazione da AWS.
                </Alert>
            )}
            <Card>
                <Card.Body className="sid-card-body--spaced">
                    {stato && <Alert variant={stato.ok ? 'success' : 'warning'} className="text-dark">{stato.testo}</Alert>}
                    {problema && (
                        <Alert variant="warning" className="text-dark">
                            Con questa configurazione il sistema non può inviare le email del portale SportID: {problema}.
                        </Alert>
                    )}

                    <Form.Check
                        id="enabled"
                        type="checkbox"
                        className="mb-4"
                        checked={form.enabled}
                        onChange={(e) => aggiorna('enabled', e.target.checked)}
                        label="Usa questa configurazione per inviare le email, se non configurato il sistema non può inviare email"
                    />

                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Label htmlFor="from_address">Indirizzo mittente</Form.Label>
                            <Form.Control id="from_address" type="email" value={form.from_address} onChange={(e) => aggiorna('from_address', e.target.value)} />
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="from_name">Nome mittente</Form.Label>
                            <Form.Control id="from_name" value={form.from_name} onChange={(e) => aggiorna('from_name', e.target.value)} />
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="host">Server SMTP</Form.Label>
                            <Form.Control id="host" value={form.host} onChange={(e) => aggiorna('host', e.target.value)} />
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="port">Porta</Form.Label>
                            <Form.Control id="port" type="number" value={form.port ?? ''} onChange={(e) => aggiorna('port', e.target.value ? Number(e.target.value) : null)} />
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="username">Utente</Form.Label>
                            <Form.Control id="username" autoComplete="off" value={form.username} onChange={(e) => aggiorna('username', e.target.value)} />
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="password">Password</Form.Label>
                            <Form.Control id="password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => aggiorna('password', e.target.value)} />
                            <Form.Text className="text-muted">{passwordSalvata ? 'Lasciala vuota per non cambiarla.' : 'Nessuna password salvata.'}</Form.Text>
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="encryption">Cifratura</Form.Label>
                            <Form.Select id="encryption" value={form.encryption} onChange={(e) => aggiorna('encryption', e.target.value)}>
                                <option value="tls">TLS</option>
                                <option value="ssl">SSL</option>
                            </Form.Select>
                        </Col>
                    </Row>

                    {esitoTest && <Alert variant={esitoTest.ok ? 'success' : 'danger'} className="text-dark mt-4 mb-0">{esitoTest.testo}</Alert>}

                    <Row className="g-4 mt-1">
                        <Col md={6}>
                            <Form.Label htmlFor="test_recipient">Invia un'email di test a (un tuo indirizzo)</Form.Label>
                            <Form.Control id="test_recipient" type="email" value={form.test_recipient} onChange={(e) => aggiorna('test_recipient', e.target.value)} />
                            <Form.Text className="text-muted">
                                A differenza di «Testa connessione» (solo l'handshake SMTP), invia davvero un'email: l'unico modo per essere
                                certi che l'invio funzioni, non solo che le credenziali siano corrette. Si salva con «Salva».
                            </Form.Text>
                        </Col>
                    </Row>
                    {esitoInvio && <Alert variant={esitoInvio.ok ? 'success' : 'danger'} className="text-dark mt-4 mb-0">{esitoInvio.testo}</Alert>}
                </Card.Body>
            </Card>
            <div className="d-flex justify-content-end gap-4 mt-4">
                <Button type="button" className="btn btn-dark light" onClick={prova} disabled={!!inCorso}>Testa connessione</Button>
                <Button type="button" className="btn btn-dark light" onClick={invia} disabled={!!inCorso}>Invia email di test</Button>
                <Button type="submit" className="btn btn-primary" disabled={!!inCorso}>Salva</Button>
            </div>
        </Form>
    );
};

export default PostaInUscita;
